import { LegalPage } from "@/components/LegalPage";
import { LEGAL_UPDATED, useLegalLang } from "@/lib/legalLocale";
import {
  OPERATOR_NAME,
  PRODUCT_NAME,
  PRODUCT_NAME_ZH,
  PUBLIC_PRIVACY_URL,
  PUBLIC_TERMS_URL,
  SUPPORT_EMAIL,
} from "@/lib/publicLinks";

const linkClass = "text-foreground underline underline-offset-2 break-all";

export default function Terms() {
  const lang = useLegalLang();
  return (
    <LegalPage title={lang === "en" ? "Terms of Service" : "服務條款"}>
      {lang === "en" ? <TermsEn /> : <TermsZh />}
    </LegalPage>
  );
}

function TermsEn() {
  return (
    <>
      <header className="space-y-2">
        <p className="text-foreground font-medium">
          Welcome to {PRODUCT_NAME} ({PRODUCT_NAME_ZH}). By using the service you agree to these terms and the Privacy Policy.
        </p>
        <p>Last updated: {LEGAL_UPDATED}</p>
        <p>
          Public URL:{" "}
          <a className={linkClass} href={PUBLIC_TERMS_URL}>
            {PUBLIC_TERMS_URL}
          </a>
        </p>
      </header>

      <section className="space-y-2">
        <h2 className="text-base font-bold text-foreground">1. Provider</h2>
        <ul className="list-disc pl-5 space-y-1">
          <li>Service: {PRODUCT_NAME} ({PRODUCT_NAME_ZH})</li>
          <li>Operator: {OPERATOR_NAME}</li>
          <li>
            Email:{" "}
            <a className="text-foreground underline underline-offset-2" href={`mailto:${SUPPORT_EMAIL}`}>
              {SUPPORT_EMAIL}
            </a>
          </li>
        </ul>
      </section>

      <section className="space-y-2">
        <h2 className="text-base font-bold text-foreground">2. The service</h2>
        <p>
          The service is a personal and small-group finance tool. It includes bookkeeping, recurring entries, financial to-dos, accounts and assets, budgets, investment records, savings jars, loan tracking, and related summaries and reminders.
          We may add, change, or remove features. An internet connection is required.
        </p>
        <p>
          The service is provided as is. We do not promise that it will be uninterrupted, error-free, or available in real time, and we do not offer a service-level agreement.
          Maintenance, upgrades, networks, or third-party outages can make it temporarily unavailable.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="text-base font-bold text-foreground">3. Age and accounts</h2>
        <ul className="list-disc pl-5 space-y-1">
          <li>
            You must be at least <span className="text-foreground font-medium">13</span>, or older if your local law requires it.
          </li>
          <li>You must provide accurate information and keep your credentials and device secure.</li>
          <li>You may not impersonate someone else, bypass verification, or access the service without authorization.</li>
          <li>If you believe an account was taken over, contact us and update the password or revoke the third-party sign-in.</li>
          <li>You may delete the account in the app. Some data may take a reasonable time to finish deleting, as described in the Privacy Policy.</li>
        </ul>
      </section>

      <section className="space-y-2">
        <h2 className="text-base font-bold text-foreground">4. Test and release builds</h2>
        <p>
          If you use the service through TestFlight, an internal test, or another preview, features, the interface, and data handling may change.
          Some features may be unstable, and test data may be adjusted, reset, or impossible to restore fully during maintenance.
          The released app can also keep changing. The latest version and these terms control.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="text-base font-bold text-foreground">5. Your responsibilities</h2>
        <p>You agree not to:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Break the law, infringe someone else’s rights, or distribute illegal or harmful content.</li>
          <li>Interfere with the service, or probe, scrape, or reverse engineer it without authorization.</li>
          <li>Use the service to assist money laundering, fraud, or other illegal financial activity.</li>
          <li>Maliciously alter or delete records in a shared account that others reasonably rely on.</li>
        </ul>
        <p>You are responsible for everything you enter, import, or share.</p>
      </section>

      <section className="space-y-2">
        <h2 className="text-base font-bold text-foreground">6. Financial information</h2>
        <ul className="list-disc pl-5 space-y-2">
          <li>
            Figures, charts, budgets, and summaries are for your own organization and reference. They are{" "}
            <span className="text-foreground font-medium">not</span> investment, tax, legal, or other professional advice.
          </li>
          <li>Exchange rates, quotes, and other market data may be delayed, incomplete, or different from a broker or bank. Use the official source.</li>
          <li>You should check important amounts and decisions yourself, and you accept the risk of using the service.</li>
        </ul>
      </section>

      <section className="space-y-2">
        <h2 className="text-base font-bold text-foreground">7. Shared accounts</h2>
        <p>
          Shared accounts, invites, and splits depend on trust among the people involved. Manage invites and permissions carefully.
          Disputes caused by a collaborator, a mistaken edit, or a lost device should be resolved by those users. We may help where it is technically possible, but we do not promise to restore every past state.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="text-base font-bold text-foreground">8. AI and automation</h2>
        <p>
          Voice entry, AI import, and the new-user flow that drafts a book from your description can be wrong. Before you save, check the amount, account, category, and parties.
          On iOS, speech is converted to text by Apple’s speech recognition. The text is then sent to Google Gemini to produce a draft.
          Do not submit secrets you do not want those services to process.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="text-base font-bold text-foreground">9. Intellectual property</h2>
        <p>
          Except for data you upload or enter, the software, interface, marks, and other content belong to the operator or the lawful owner.
          You may not copy, adapt, distribute, or commercially exploit them without permission.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="text-base font-bold text-foreground">10. Changes, suspension, and ending the service</h2>
        <p>
          We may change, suspend, or stop all or part of the service for maintenance, security, legal, or operational reasons.
          If a change has a major effect, we will tell you beforehand or afterward when that is reasonable. You may stop using the service and delete your account at any time.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="text-base font-bold text-foreground">11. Limitation of liability</h2>
        <p>
          To the fullest extent the law allows, the operator is not liable for indirect, incidental, special, or consequential damages from using or being unable to use the service, including lost data or lost profits.
          If the law still requires liability, it is limited to the amount you actually paid us for the dispute. While the service is free, that cap may be zero.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="text-base font-bold text-foreground">12. Updates to these terms</h2>
        <p>
          We may update these terms. The new text will be posted at the public URL above. Material changes may also be announced in the app, by email, or by another reasonable method.
          If you keep using the service after an update, you have seen and agree to the new terms. If you do not agree, stop using the service and delete your account.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="text-base font-bold text-foreground">13. Law and disputes</h2>
        <p>
          These terms are governed by the laws of the Republic of China (Taiwan). Disputes should first be discussed in good faith.
          If that fails, the Taipei District Court is the court of first instance, unless a mandatory rule says otherwise.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="text-base font-bold text-foreground">14. Other</h2>
        <p>
          If one clause is held invalid, the rest stay in effect. Not enforcing a right does not waive it.
          Apple, Google, and other platform companies are not parties to these terms and, except where the law or their policies say otherwise, do not guarantee the service to you.
          Privacy is covered separately:{" "}
          <a className={linkClass} href={PUBLIC_PRIVACY_URL}>
            {PUBLIC_PRIVACY_URL}
          </a>
        </p>
      </section>
    </>
  );
}

function TermsZh() {
  return (
    <>
      <header className="space-y-2">
        <p className="text-foreground font-medium">
          歡迎使用 {PRODUCT_NAME_ZH}（{PRODUCT_NAME}）。使用本服務即表示您已閱讀、瞭解並同意本服務條款與隱私權政策。
        </p>
        <p>最後更新日期：{LEGAL_UPDATED}</p>
        <p>
          公開網址：{" "}
          <a className={linkClass} href={PUBLIC_TERMS_URL}>
            {PUBLIC_TERMS_URL}
          </a>
        </p>
      </header>

      <section className="space-y-2">
        <h2 className="text-base font-bold text-foreground">1. 服務提供者</h2>
        <ul className="list-disc pl-5 space-y-1">
          <li>服務名稱：{PRODUCT_NAME_ZH}（{PRODUCT_NAME}）</li>
          <li>營運者：{OPERATOR_NAME}</li>
          <li>
            聯絡信箱：{" "}
            <a className="text-foreground underline underline-offset-2" href={`mailto:${SUPPORT_EMAIL}`}>
              {SUPPORT_EMAIL}
            </a>
          </li>
        </ul>
      </section>

      <section className="space-y-2">
        <h2 className="text-base font-bold text-foreground">2. 服務內容與提供方式</h2>
        <p>
          本服務提供個人與小型協作情境下的理財工具，包括但不限於記帳、週期性記帳、財務待辦、資產與帳戶管理、預算、投資紀錄、存錢罐、借貸追蹤，以及相關分析與提醒。
          我們可能不時新增、調整或下線部分功能。本服務需連網使用。
        </p>
        <p>
          本服務依「現況（as is）」提供，我們不保證服務不中斷、無錯誤、即時可用，亦不提供特定服務水準協議（SLA）。
          維護、升級、網路或第三方服務異常可能造成暫時無法使用。
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="text-base font-bold text-foreground">3. 年齡與帳號資格</h2>
        <ul className="list-disc pl-5 space-y-1">
          <li>
            您必須年滿
            <span className="text-foreground font-medium"> 13 歲</span>
            （或您所在地法律要求之更高年齡）始得使用本服務。
          </li>
          <li>您應提供正確資料，並妥善保管登入憑證與裝置安全。</li>
          <li>您不得冒用他人身分、規避驗證，或以未經授權方式存取本服務。</li>
          <li>若發現帳號遭盜用，請立即聯絡我們並視情況更新密碼／撤銷第三方授權。</li>
          <li>您可隨時在 App 內刪除帳號；刪除後部分資料可能依隱私權政策所述於合理期間內完成清除。</li>
        </ul>
      </section>

      <section className="space-y-2">
        <h2 className="text-base font-bold text-foreground">4. 測試、預覽與正式版本</h2>
        <p>
          若您透過 TestFlight、內部測試或其他預覽管道使用本服務，功能、介面與資料處理方式可能隨時變更；
          部分功能可能不穩定，且在維護或升級過程中，測試資料可能被調整、重置或無法完整還原。
          正式上架版本仍可能持續更新，請以最新版本與本條款為準。
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="text-base font-bold text-foreground">5. 使用者義務</h2>
        <p>您同意不從事下列行為：</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>違反法令、侵害他人權利，或散布違法／有害內容。</li>
          <li>干擾、破壞服務運作，或進行未授權之探測、爬取、反向工程。</li>
          <li>將本服務用於洗錢、詐欺或其他非法金融活動之輔助。</li>
          <li>在共同帳戶中惡意竄改、刪除他人合理依賴之紀錄。</li>
        </ul>
        <p>您對自己輸入、匯入或分享的內容負全部責任。</p>
      </section>

      <section className="space-y-2">
        <h2 className="text-base font-bold text-foreground">6. 財務資訊免責</h2>
        <ul className="list-disc pl-5 space-y-2">
          <li>
            本服務提供的數字、圖表、預算與分析僅供個人整理與參考，
            <span className="text-foreground font-medium">不構成</span>
            投資、稅務、法律或任何專業建議。
          </li>
          <li>匯率、證券報價或其他市場資料可能延遲、不完整或與券商／銀行實際數字不同；請以官方來源為準。</li>
          <li>您應自行核對重要金額與決策，並自行承擔使用本服務所生之風險。</li>
        </ul>
      </section>

      <section className="space-y-2">
        <h2 className="text-base font-bold text-foreground">7. 共同帳戶與協作</h2>
        <p>
          共同帳戶、邀請與分帳功能依您與協作者之間的信任關係運作。請謹慎管理邀請與權限。
          因協作者行為、錯誤操作或裝置遺失所生之爭議，應由相關使用者自行協調；我們可在技術可行範圍內提供協助，但不保證能還原所有歷史狀態。
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="text-base font-bold text-foreground">8. AI 與自動化功能</h2>
        <p>
          語音記帳、AI 智慧匯入，以及用描述建立帳本的新用戶引導，可能產生不正確的結果。您在確認並儲存前，應自行檢查金額、帳戶、類別與對象。
          在 iOS 上，語音會先由 Apple 語音辨識轉成文字，該文字再傳送至 Google Gemini 以產生草稿。
          請勿輸入您不希望這些服務處理的機密內容。
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="text-base font-bold text-foreground">9. 智慧財產權</h2>
        <p>
          本服務之軟體、介面、商標、文案與其他內容，除您自行上傳或輸入的資料外，權利歸營運者或合法權利人所有。
          未經授權，您不得重製、改作、散布或用於商業用途。
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="text-base font-bold text-foreground">10. 服務變更、暫停與終止</h2>
        <p>
          我們得基於維護、安全、法規或營運需要，修改、暫停或終止全部或部分服務。
          若發生重大影響，我們會在合理範圍內事先或事後告知。您也可隨時停止使用並刪除帳號。
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="text-base font-bold text-foreground">11. 責任限制</h2>
        <p>
          在法律允許的最大範圍內，對於因使用或無法使用本服務所生之間接、附帶、特別或衍生性損害（包括資料遺失、利潤損失），
          營運者不負賠償責任。若依法仍須負責，責任上限以您就相關爭議事由已實際支付予我們的費用為限（目前免費提供時，該上限得為零）。
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="text-base font-bold text-foreground">12. 條款更新</h2>
        <p>
          我們可能不時更新本服務條款。更新後之條款將公布於上述公開網址，並得透過 App 內提示、Email 或其他合理方式告知重大變更。
          若您於條款更新後繼續使用本服務，即表示您知悉並同意更新後的內容；若您不同意，請停止使用並刪除帳號。
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="text-base font-bold text-foreground">13. 準據法與爭議</h2>
        <p>
          本條款以中華民國（臺灣）法律為準據法。因本條款或本服務所生之爭議，雙方應先善意協商；協商不成時，
          以臺灣臺北地方法院為第一審管轄法院，但法律另有強制規定者從其規定。
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="text-base font-bold text-foreground">14. 其他</h2>
        <p>
          若本條款任一條款經認定無效，不影響其餘條款之效力。我們未行使某項權利，不代表放棄該權利。
          Apple、Google 等平台業者並非本條款當事人，除依法或平台政策另有規定外，不就本服務對您負擔保證責任。
          隱私相關事項請另參閱隱私權政策：{" "}
          <a className={linkClass} href={PUBLIC_PRIVACY_URL}>
            {PUBLIC_PRIVACY_URL}
          </a>
        </p>
      </section>
    </>
  );
}
